import { Box } from '@mui/material';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import DVHLineChart from '~/components/Charts/DVHLineChart';
import { getDoseLabel } from '~/containers/viewer/explorer-sidebar/WorkflowSidebar/DoseStep/doseUtils';
import { useCurrentDose } from '~/hooks/dose/useCurrentDose';
import useDose from '~/hooks/patient/dose/useDose';
import { useDvhCurves } from '~/hooks/patient/dose/useDvhCurves';
import { useEmphasizedRoiNumber } from '~/hooks/patient/dose/useEmphasizedRoiNumber';
import {
    showDoseStatisticsTableState,
    showDVHViewComponentState,
} from '~/state/atoms/viewerAtoms';
import { GridPanel } from '~/viewportGrid';
import DoseStatisticsExportMenu from './DoseStatisticsExportMenu';
import DoseStatisticsPane from './DoseStatisticsPane';
import DoseTablesPane from './DoseTablesPane';
import { useDoseTableSelection } from './useDoseTableSelection';
import { seriesInstanceUidOf } from '~/domain/dicom/uids';

/**
 * The DVH chart and the dose statistics table as grid panels, in reading
 * order, none while hidden: each is offered while its display atom says
 * shown and a dose is selected, and both read one set of curves, computed
 * for the selected dose only while a panel is shown. The tiles name the
 * dose they read, and hang on the row of the image the dose's structure
 * set belongs to, the row that draws the dose; a set with no image names
 * no host row.
 *
 * Where the optimization objectives are a choice, the statistics tile
 * holds them as a second table behind a tab strip. The tile is named
 * after the table it shows and its export control offers that table's
 * downloads. The choice is held here, so it outlives the tile's body.
 */
export function useDosePanelTiles(): ReadonlyArray<GridPanel> {
    const intl = useIntl();
    const showChart = useRecoilValue(showDVHViewComponentState) === true;
    const showTable = useRecoilValue(showDoseStatisticsTableState) === true;
    const { dose } = useDose();
    const currentDose = useCurrentDose();
    const { curves, computing } = useDvhCurves(showChart || showTable);
    const emphasizedRoiNumber = useEmphasizedRoiNumber();
    const { table, selectTable, objectivesOffered, objectivesAvailable } =
        useDoseTableSelection();
    const hostSeriesUid = seriesInstanceUidOf(
        currentDose?.structureSet?.image?.seriesMetadata.seriesInstanceUID
    );
    const subject = dose
        ? getDoseLabel(dose.planLabel, dose.summationType)
        : undefined;

    return useMemo((): GridPanel[] => {
        if (!dose) {
            return [];
        }
        const list: GridPanel[] = [];
        if (showChart) {
            list.push({
                kind: 'dvh',
                title: intl.formatMessage({ id: 'dose.panel.dvh' }),
                subject,
                hostSeriesUid,
                body: ({ maximized }) => (
                    <Box
                        data-testid="dvh-line-chart-container"
                        sx={{ height: '100%', width: '100%' }}
                    >
                        {/* Maximize remounts it: scales and hover restart. */}
                        <DVHLineChart
                            key={maximized ? 'fullscreen' : 'normal'}
                            data={curves}
                            normalizationValueGy={dose.normalizationValueGy}
                            loading={computing}
                            expanded={maximized}
                            emphasizedRoiNumber={emphasizedRoiNumber}
                        />
                    </Box>
                ),
            });
        }
        if (showTable) {
            list.push({
                kind: 'dose-statistics',
                title: intl.formatMessage({
                    id:
                        table === 'objectives'
                            ? 'dose.panel.objectives'
                            : 'dose.panel.statistics',
                }),
                subject,
                hostSeriesUid,
                actions: (
                    <DoseStatisticsExportMenu curves={curves} table={table} />
                ),
                body: () =>
                    objectivesOffered ? (
                        <DoseTablesPane
                            curves={curves}
                            table={table}
                            onSelectTable={selectTable}
                            objectivesAvailable={objectivesAvailable}
                        />
                    ) : (
                        <DoseStatisticsPane curves={curves} />
                    ),
            });
        }
        return list;
    }, [
        intl,
        dose,
        showChart,
        showTable,
        subject,
        hostSeriesUid,
        curves,
        computing,
        emphasizedRoiNumber,
        table,
        selectTable,
        objectivesOffered,
        objectivesAvailable,
    ]);
}
